import { parseISO, startOfDay, subDays } from "date-fns";
import { RangeValueType } from "rc-picker/lib/PickerInput/RangePicker";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { TimeRange } from "../types";
import { useURLParamState } from "./useURLParamState";

/**
 * Safely parse a date from URL params with error handling
 */
const getTime = (
  search: URLSearchParams,
  param: string,
  defaultValue?: Date
): Date | null => {
  const raw = search.get(param);
  if (!raw) return defaultValue ?? null;

  try {
    const value = parseISO(raw);
    if (isNaN(value.getTime())) {
      console.warn(`Invalid date in URL param "${param}": "${raw}"`);
      return null;
    }
    return value;
  } catch (error) {
    console.error(`Failed to parse date from "${param}":`, error);
    return null;
  }
};

/**
 * Set or delete a date parameter in URLSearchParams
 */
const setTime = (
  search: URLSearchParams,
  param: string,
  time: Date | null
): void => {
  if (time === null) {
    search.delete(param);
  } else {
    search.set(param, time.toISOString());
  }
};

/**
 * Hook to manage a date range in URL parameters
 */
const useTimeRangeQuery = (
  [startParam, endParam]: [string, string],
  options?: { startDefault?: Date; endDefault?: Date }
): [TimeRange, (value: RangeValueType<Date> | null | undefined) => void] => {
  const [search, setSearch] = useSearchParams();

  const range = useMemo<TimeRange>(
    () => [
      getTime(search, startParam, options?.startDefault),
      getTime(search, endParam, options?.endDefault),
    ],
    [endParam, options?.endDefault, options?.startDefault, search, startParam]
  );

  const setRange = useCallback(
    (input: RangeValueType<Date> | null | undefined): void => {
      const startValue = input?.[0] ?? null;
      const endValue = input?.[1] ?? null;

      const next = new URLSearchParams(search);
      setTime(next, startParam, startValue);
      setTime(next, endParam, endValue);
      setSearch(next);
    },
    [endParam, search, setSearch, startParam]
  );

  return [range, setRange];
};

/**
 * Central hook for managing all request filter state via URL parameters.
 * Provides search, date ranges, and dropdown filters.
 * @param options - The options for the filters
 * @param options.defaultRequestedStart - The default start date for the requested filter (defaults to 30 days ago if not provided)
 */
export const useRequestFilters = (options?: {
  defaultRequestedStart?: Date;
}) => {
  const [, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useURLParamState("q");

  const startDefault = useMemo(
    () => options?.defaultRequestedStart ?? startOfDay(subDays(new Date(), 30)),
    [options?.defaultRequestedStart]
  );

  const [requestedFilter, setRequestedFilter] = useTimeRangeQuery(
    ["requestedStart", "requestedEnd"],
    { startDefault }
  );

  const [approvedFilter, setApprovedFilter] = useTimeRangeQuery([
    "approvedStart",
    "approvedEnd",
  ]);

  const [requestorFilter, setRequestorFilter] = useURLParamState("requestor");
  const [approverFilter, setApproverFilter] = useURLParamState("approver");
  const [resourceFilter, setResourceFilter] = useURLParamState("resource");
  const [parentFilter, setParentFilter] = useURLParamState("parent");

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return {
    // Search bar filters
    searchQuery,
    setSearchQuery,
    // Date picker filters
    requestedFilter,
    setRequestedFilter,
    approvedFilter,
    setApprovedFilter,
    // Dropdown filters
    requestorFilter,
    setRequestorFilter,
    approverFilter,
    setApproverFilter,
    resourceFilter,
    setResourceFilter,
    parentFilter,
    setParentFilter,
    // Utility functions
    resetFilters,
  };
};
